import { ArrowLeft, ArrowRight, ChevronDown, Maximize2, Minimize2, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { IconButton } from "../../components/IconButton/IconButton";
import { MenuOption } from "../../components/Menu/MenuOption";
import { SegmentedControl, type SegmentedControlOption } from "../../components/SegmentedControl/SegmentedControl";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { useListbox } from "../../hooks/useListbox";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";

export interface PreviewPanelItem {
  key: string;
  name: string;

  meta?: string;
}

/** Every built-in string, so apps can translate them. Templated ones take the values to insert. */
export interface PreviewPanelLabels {
  outputs: string;
  close: string;
  previous: string;
  next: string;
  position: (current: number, total: number) => string;
  view: string;
  expand: string;
  collapse: string;
  resize: string;
}

export const DEFAULT_PREVIEW_PANEL_LABELS: PreviewPanelLabels = {
  outputs: "Outputs in this chat",
  close: "Close",
  previous: "Previous output",
  next: "Next output",
  position: (current, total) => `Output ${current} of ${total}`,
  view: "Output view",
  expand: "Expand",
  collapse: "Collapse",
  resize: "Resize panel",
};

const RESIZE_STEP = 24;

export interface PreviewPanelProps {
  artifacts: PreviewPanelItem[];
  activeIndex: number;
  onSelectArtifact: (index: number) => void;
  tabs: SegmentedControlOption<string>[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onClose: () => void;

  actions?: ReactNode;
  children?: ReactNode;

  /** Overrides for the built-in strings (see DEFAULT_PREVIEW_PANEL_LABELS). */
  labels?: Partial<PreviewPanelLabels>;

  /** Starting width in px before any resizing. */
  defaultWidth?: number;
  /** How far the panel can shrink while dragging the resize divider. */
  minWidth?: number;
  /** How far the panel can grow while dragging the resize divider. */
  maxWidth?: number;
  /** Starts expanded to fill the chat area instead of docked at defaultWidth. */
  defaultExpanded?: boolean;
}

export function PreviewPanel({
  artifacts,
  activeIndex,
  onSelectArtifact,
  tabs,
  activeTab,
  onTabChange,
  onClose,
  actions,
  children,
  labels,
  defaultWidth = 380,
  minWidth = 320,
  maxWidth = 720,
  defaultExpanded = false,
}: Readonly<PreviewPanelProps>) {
  const l = { ...DEFAULT_PREVIEW_PANEL_LABELS, ...labels };
  const active = artifacts[activeIndex];
  const [width, setWidth] = useState(defaultWidth);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const clampWidth = (next: number) => Math.min(maxWidth, Math.max(minWidth, next));

  const handleDividerPointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    dragStartRef.current = { startX: event.clientX, startWidth: width };
    setIsDragging(true);
  };

  // The panel sits on the right, so ArrowLeft widens it.
  const handleDividerKeyDown = (event: React.KeyboardEvent) => {
    const next = {
      ArrowLeft: width + RESIZE_STEP,
      ArrowRight: width - RESIZE_STEP,
      Home: minWidth,
      End: maxWidth,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    setWidth(clampWidth(next));
  };

  useEffect(() => {
    if (!isDragging) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const handlePointerMove = (event: PointerEvent) => {
      if (!dragStartRef.current) return;
      const delta = dragStartRef.current.startX - event.clientX;
      setWidth(Math.min(maxWidth, Math.max(minWidth, dragStartRef.current.startWidth + delta)));
    };
    const handlePointerUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDragging, minWidth, maxWidth]);
  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  } = useListbox({
    id: "artifact-switcher",
    options: artifacts.map((artifact) => ({ value: artifact.key })),
    value: active?.key,
    onChange: (key) => {
      const index = artifacts.findIndex((artifact) => artifact.key === key);
      if (index >= 0) onSelectArtifact(index);
    },
  });
  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({ isOpen, anchorRef: triggerRef });

  return (
    <aside
      style={isExpanded ? undefined : { width }}
      className={[
        "flex shrink-0 flex-col bg-surface-primary",
        isExpanded
          ? "absolute inset-y-3 left-3 right-3 z-10 ml-auto max-w-[1200px] rounded-2xl border border-border-subtle shadow-xl"
          : "relative border-l border-border-subtle",
      ].join(" ")}
    >
      {!isExpanded && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={l.resize}
          aria-valuenow={width}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          tabIndex={0}
          onPointerDown={handleDividerPointerDown}
          onKeyDown={handleDividerKeyDown}
          className="group absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none outline-none"
        >
          <div
            className={[
              "mx-auto h-full w-px transition-colors group-hover:bg-border-strong group-focus-visible:w-0.5 group-focus-visible:bg-border-focus",
              isDragging ? "bg-border-strong" : "bg-transparent",
            ].join(" ")}
          />
        </div>
      )}
      <div className="border-b border-border-subtle">
        <div className="flex items-center gap-1 px-2.5 pt-2 pb-1">
          <div ref={containerRef} className="relative min-w-0 flex-1">
            <button
              ref={triggerRef}
              type="button"
              role="combobox"
              aria-label={l.outputs}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-controls={isOpen ? listboxId : undefined}
              aria-activedescendant={isOpen ? optionId(highlightedIndex) : undefined}
              onClick={() => (isOpen ? setIsOpen(false) : openAt(activeIndex))}
              onKeyDown={handleTriggerKeyDown}
              className="inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-surface-secondary btn-focus-ring"
            >
              <span className="truncate text-sm font-semibold text-content-primary">{active?.name}</span>
              <ChevronDown size={13} className="shrink-0 text-content-tertiary" />
            </button>
            {isOpen && (
              <div
                ref={panelRef}
                role="listbox"
                id={listboxId}
                aria-label={l.outputs}
                style={panelStyle}
                className="z-[110] max-h-64 w-72 overflow-y-auto rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
              >
                <div className="px-3 pt-1.5 pb-1 [font:var(--type-eyebrow)] uppercase tracking-wide text-content-tertiary">
                  {l.outputs}
                </div>
                {artifacts.map((artifact, index) => (
                  <MenuOption
                    key={artifact.key}
                    role="option"
                    id={optionId(index)}
                    optionRef={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    highlighted={index === highlightedIndex}
                    selected={index === activeIndex}
                    trailing={
                      artifact.meta ? (
                        <span className="[font:var(--type-micro-sm)] text-content-tertiary">{artifact.meta}</span>
                      ) : (
                        false
                      )
                    }
                    onHighlight={() => openAt(index)}
                    onSelect={() => commitSelection(index)}
                  >
                    {artifact.name}
                  </MenuOption>
                ))}
              </div>
            )}
          </div>
          <Tooltip content={isExpanded ? l.collapse : l.expand}>
            <IconButton
              icon={isExpanded ? <Minimize2 /> : <Maximize2 />}
              aria-label={isExpanded ? l.collapse : l.expand}
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded((expanded) => !expanded)}
            />
          </Tooltip>
          <Tooltip content={l.close}>
            <IconButton icon={<X />} aria-label={l.close} variant="ghost" size="sm" onClick={onClose} />
          </Tooltip>
        </div>
        <div className="flex items-center gap-1 px-2.5 pb-2">
          <Tooltip content={l.previous}>
            <IconButton
              icon={<ArrowLeft />}
              aria-label={l.previous}
              variant="ghost"
              size="sm"
              disabled={activeIndex === 0}
              onClick={() => onSelectArtifact(activeIndex - 1)}
            />
          </Tooltip>
          <span className="whitespace-nowrap [font:var(--type-micro)] tabular-nums text-content-tertiary">
            {l.position(activeIndex + 1, artifacts.length)}
          </span>
          <Tooltip content={l.next}>
            <IconButton
              icon={<ArrowRight />}
              aria-label={l.next}
              variant="ghost"
              size="sm"
              disabled={activeIndex === artifacts.length - 1}
              onClick={() => onSelectArtifact(activeIndex + 1)}
            />
          </Tooltip>
          <span className="flex-1" />
          <SegmentedControl options={tabs} value={activeTab} onChange={onTabChange} aria-label={l.view} />
        </div>
      </div>
      <div
        role="region"
        aria-label={active?.name}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-reachable (WCAG 2.1.1, axe scrollable-region-focusable); a PDF's pages have no focusable content.
        tabIndex={0}
        className="flex-1 overflow-y-auto px-5 py-5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus"
      >
        {children}
      </div>
      {actions && <div className="flex items-center gap-2 border-t border-border-subtle px-4 py-3">{actions}</div>}
    </aside>
  );
}
